/**
 * Which frame of an imported page is chosen, and which one is being rewritten.
 *
 * Shared by the page and its inspector: a frame picked on the page is the one
 * the inspector talks about, and "edit" in the inspector opens the editor on
 * the page. One frame at a time for the whole report, like the section
 * selection it lives under.
 */

import { ref } from 'vue'

interface FrameRef {
  readonly sectionId: string
  readonly frameId: string
}

const chosen = ref<FrameRef | null>(null)
const editing = ref<FrameRef | null>(null)
/** Whether the frames are outlined on the page, to see how it was read. */
const outlined = ref(false)
/** Rewritten frames whose text runs past their box, by "section:frame", with how many lines. */
const overflowing = ref<ReadonlyMap<string, number>>(new Map())

function same(a: FrameRef | null, sectionId: string, frameId: string): boolean {
  return a !== null && a.sectionId === sectionId && a.frameId === frameId
}

function keyOf(sectionId: string, frameId: string): string {
  return `${sectionId}:${frameId}`
}

export function useFrameSelection() {
  return {
    outlined,
    overflowOf: (sectionId: string, frameId: string): number =>
      overflowing.value.get(keyOf(sectionId, frameId)) ?? 0,
    setOverflow(sectionId: string, frameId: string, lines: number): void {
      const key = keyOf(sectionId, frameId)
      if ((overflowing.value.get(key) ?? 0) === lines) return
      const next = new Map(overflowing.value)
      if (lines > 0) next.set(key, lines)
      else next.delete(key)
      overflowing.value = next
    },
    choose(sectionId: string, frameId: string): void {
      chosen.value = { sectionId, frameId }
    },
    /**
     * The frame chosen on a page, or null. A plain read of reactive state: call
     * it inside a computed, with the page's id as it is *then*, since a
     * component showing one page may be reused for another.
     */
    chosenIn: (sectionId: string): string | null =>
      chosen.value?.sectionId === sectionId ? chosen.value.frameId : null,
    isChosen: (sectionId: string, frameId: string): boolean =>
      same(chosen.value, sectionId, frameId),
    edit(sectionId: string, frameId: string): void {
      chosen.value = { sectionId, frameId }
      editing.value = { sectionId, frameId }
    },
    isEditing: (sectionId: string, frameId: string): boolean =>
      same(editing.value, sectionId, frameId),
    stopEditing(): void {
      editing.value = null
    },
  }
}
